import { useAuth } from '../contexts/AuthContext';
import { getEffectiveRole } from '../utils/roles';

/**
 * Custom hook to check user permissions
 * 
 * @returns {Object} Permission checking functions
 */
export const usePermissions = () => {
    const { user } = useAuth();
    const effectiveRole = getEffectiveRole(user);
    const permissionAliases = {
        // Leads
        'leads.view': ['viewlead', 'view_leads', 'lead.view', 'leads_view'],
        'leads.create': ['createlead', 'create_lead', 'lead.create', 'leads_create'],
        'leads.edit': ['editlead', 'edit_lead', 'lead.edit', 'leads_edit'],
        'leads.delete': ['deletelead', 'delete_lead', 'lead.delete', 'leads_delete'],
        'leads.assign': ['assignlead', 'assign_lead', 'lead.assign', 'leads_assign'],
        'leads.bulk_import': ['bulkimportlead', 'import_leads', 'lead.bulk_import', 'leads_bulk_import'],
        'leads.export': ['exportlead', 'export_leads', 'lead.export', 'leads_export'],
        'leads.status': ['updatestatus', 'update_status', 'lead.status', 'leads_status', 'change_status', 'status_update'],

        // Users & Roles
        'users.view': ['viewuser', 'view_users', 'user.view'],
        'users.create': ['createuser', 'create_user', 'user.create'],
        'users.edit': ['edituser', 'edit_user', 'user.edit'],
        'users.delete': ['deleteuser', 'delete_user', 'user.delete'],
        'users.view_profile': ['viewprofile', 'view_profile', 'profile.view'],
        'roles.view': ['viewrole', 'view_roles', 'role.view'],
        'roles.create': ['createrole', 'create_role', 'role.create'],
        'roles.edit': ['editrole', 'edit_role', 'role.edit'],
        'roles.delete': ['deleterole', 'delete_role', 'role.delete'],
        'permissions.view': ['viewpermission', 'view_permissions'],
        'permissions.manage': ['managepermission', 'manage_permissions', 'permissions.assign'],

        // Projects & Properties
        'projects.view': ['viewproject', 'view_projects', 'project.view'],
        'projects.create': ['createproject', 'create_project', 'project.create'],
        'projects.edit': ['editproject', 'edit_project', 'project.edit'],
        'projects.delete': ['deleteproject', 'delete_project', 'project.delete'],
        'properties.view': ['viewproperty', 'view_properties', 'property.view'],
        'properties.create': ['createproperty', 'create_property', 'property.create'],
        'properties.edit': ['editproperty', 'edit_property', 'property.edit'],
        'properties.delete': ['deleteproperty', 'delete_property', 'property.delete'],
        'plots.view': ['viewplot', 'view_plots', 'plot.view'],
        'plots.create': ['createplot', 'create_plot', 'plot.create'],
        'plots.edit': ['editplot', 'edit_plot', 'plot.edit'],
        'plots.delete': ['deleteplot', 'delete_plot', 'plot.delete'],
        'inventory.view': ['viewinventory', 'view_inventory'],
        'inventory.manage': ['manageinventory', 'manage_inventory'],

        // Customers & Deals
        'customers.view': ['viewcustomer', 'view_customers', 'customer.view'],
        'customers.create': ['createcustomer', 'create_customer', 'customer.create'],
        'customers.edit': ['editcustomer', 'edit_customer', 'customer.edit'],
        'customers.delete': ['deletecustomer', 'delete_customer', 'customer.delete'],
        'deals.view': ['viewdeal', 'view_deals', 'deal.view'],
        'deals.create': ['createdeal', 'create_deal', 'deal.create'],
        'deals.edit': ['editdeal', 'edit_deal', 'deal.edit'],
        'deals.delete': ['deletedeal', 'delete_deal', 'deal.delete'],

        // Activities & Interactions
        'followups.view': ['viewfollowup', 'view_followups', 'followup.view'],
        'followups.create': ['createfollowup', 'create_followup', 'followup.create'],
        'followups.edit': ['editfollowup', 'edit_followup', 'followup.edit'],
        'followups.delete': ['deletefollowup', 'delete_followup', 'followup.delete'],
        'sitevisits.view': ['viewsitevisit', 'view_sitevisits', 'sitevisit.view'],
        'sitevisits.create': ['createsitevisit', 'create_sitevisits', 'sitevisit.create'],
        'sitevisits.edit': ['editsitevisit', 'edit_sitevisits', 'sitevisit.edit'],
        'sitevisits.delete': ['deletesitevisit', 'delete_sitevisits', 'sitevisit.delete'],
        'communications.view': ['viewcommunication', 'view_communications'],
        'communications.send': ['sendcommunication', 'send_communications'],
        'calls.view': ['viewcall', 'view_calls', 'call.view'],
        'calls.create': ['createcall', 'create_call', 'call.create'],
        'calls.edit': ['editcall', 'edit_call', 'call.edit'],
        'calls.delete': ['deletecall', 'delete_call', 'call.delete'],

        // System & Advanced Features
        'dashboard.view': ['viewdashboard', 'view_dashboard'],
        'reports.view': ['viewreport', 'view_reports', 'report.view'],
        'reports.export': ['exportreport', 'export_reports', 'report.export'],
        'reports.schedule': ['schedulereport', 'schedule_reports'],
        'settings.view': ['viewsetting', 'view_settings'],
        'settings.edit': ['editsetting', 'edit_settings'],
        'postsales.view': ['viewpostsales', 'view_postsales'],
        'postsales.manage': ['managepostsales', 'manage_postsales'],
        'marketing.view': ['viewmarketing', 'view_marketing'],
        'marketing.manage': ['managemarketing', 'manage_marketing'],
        'tracking.view': ['viewtracking', 'view_tracking'],
        'brokerportal.view': ['viewbrokerportal', 'view_brokerportal'],
        'brokerportal.manage': ['managebrokerportal', 'manage_brokerportal'],
        'commissions.view': ['viewcommission', 'view_commissions'],
        'commissions.manage': ['managecommission', 'manage_commissions'],
        'locations.view': ['viewlocation', 'view_locations', 'location.view'],
        'locations.create': ['createlocation', 'create_location', 'location.create'],
        'locations.edit': ['editlocation', 'edit_location', 'location.edit'],
        'locations.delete': ['deletelocation', 'delete_location', 'location.delete'],
        'brokers.view': ['viewbroker', 'view_brokers', 'broker.view'],
        'brokers.create': ['createbroker', 'create_broker', 'broker.create'],
        'brokers.edit': ['editbroker', 'edit_broker', 'broker.edit'],
        'brokers.delete': ['deletebroker', 'delete_broker', 'broker.delete'],
        'customers.notes': ['add_customer_note', 'customer.notes'],
        'followups.status': ['update_followup_status', 'followup.status'],
        'sitevisits.status': ['update_sitevisit_status', 'sitevisit.status'],
        'inventory.status': ['update_inventory_status', 'inventory.status'],
        'postsales.bookings': ['manage_bookings', 'postsales.booking'],
        'postsales.payments': ['manage_payments', 'postsales.payment'],
        'postsales.agreements': ['manage_agreements', 'postsales.agreement'],
        'postsales.kyc': ['verify_kyc', 'postsales.kyc_verify'],
        'postsales.noc': ['manage_noc', 'postsales.noc_issue'],
        'postsales.possession': ['manage_possession', 'postsales.possession_handover'],
        'financials.view': ['view_financials', 'financial_view'],
        'financials.manage': ['manage_financials', 'financial_manage'],
        'audit.view': ['view_audit', 'audit_logs']
    };

    /**
     * Check if user has a specific permission
     * @param {string} permissionCode - Permission code to check (e.g., 'leads.view')
     * @returns {boolean}
     */
    const hasPermission = (permissionCode) => {
        if (!user) {
            return false;
        }

        const target = String(permissionCode || '').toLowerCase();

        // Super Admin has all permissions
        if (effectiveRole === 'super_admin' || user.permissions === 'all') {
            return true;
        }

        // All logged-in users have access to basic dashboard and profile view
        if (target === 'dashboard.view' || target === 'users.view_profile') {
            return true;
        }

        if (!user.permissions || !Array.isArray(user.permissions)) {
            return false;
        }

        // Check if permission exists in user's permissions array.
        // Accept both canonical RBAC codes and legacy aliases.
        const normalizedPerms = user.permissions
            .map((p) => (p == null ? '' : String(p).toLowerCase()))
            .filter(Boolean);
        const aliases = permissionAliases[target] || [];
        return normalizedPerms.includes(target) || aliases.some((alias) => normalizedPerms.includes(alias));
    };

    /**
     * Check if user has any of the specified permissions
     * @param {string[]} permissionCodes - Array of permission codes
     * @returns {boolean}
     */
    const hasAnyPermission = (permissionCodes) => {
        if (!user) {
            return false;
        }

        if (effectiveRole === 'super_admin' || user.permissions === 'all') {
            return true;
        }

        if (!user.permissions || !Array.isArray(user.permissions)) {
            return false;
        }

        return permissionCodes.some(code => hasPermission(code));
    };

    /**
     * Check if user has all of the specified permissions
     * @param {string[]} permissionCodes - Array of permission codes
     * @returns {boolean}
     */
    const hasAllPermissions = (permissionCodes) => {
        if (!user) {
            return false;
        }

        if (effectiveRole === 'super_admin' || user.permissions === 'all') {
            return true;
        }

        if (!user.permissions || !Array.isArray(user.permissions)) {
            return false;
        }

        return permissionCodes.every(code => hasPermission(code));
    };

    /**
     * Get all user permissions
     * @returns {string[]|'all'}
     */
    const getPermissions = () => {
        if (!user) {
            return [];
        }

        if (effectiveRole === 'super_admin' || user.permissions === 'all') {
            return 'all';
        }

        return user.permissions || [];
    };

    /**
     * Check if user is Super Admin
     * @returns {boolean}
     */
    const isSuperAdmin = () => {
        return user?.permissions === 'all' || effectiveRole === 'super_admin';
    };

    return {
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        getPermissions,
        isSuperAdmin
    };
};

export default usePermissions;

